// screens/accounts/ActivateRegistration.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../providers/AuthProvider';
import { useToast } from '../providers/ToastProvider';
import {
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Loader2,
  Coins,
  Shield,
} from 'lucide-react';

const ActivateRegistration = () => {
  const { colors } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { initiateRegistrationPayment, pollRegistrationStatus, refreshUser } = useAuth();

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  const stateUserId = location.state?.userId;
  const queryUserId = new URLSearchParams(location.search).get('user_id');
  const storedUserId = localStorage.getItem('registration_user_id');
  const userId = stateUserId || queryUserId || storedUserId;

  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    // Persist the id so a page refresh doesn't lose context
    if (userId) localStorage.setItem('registration_user_id', userId);
  }, [userId]);

  const handleStart = async () => {
    if (!userId) {
      setError('Missing user reference. Please sign in and try again.');
      return;
    }

    setError('');
    setBusy(true);

    try {
      const res = await initiateRegistrationPayment({ userId });

      if (!res.ok) {
        setError(res.error || 'Could not initiate payment.');
        setBusy(false);
        return;
      }

      const d = res.data || {};

      // Zero fee — account auto-activated
      if (d.zero_fee) {
        setWaiting(true);
        setStatusMessage('Activating your account…');
        // Poll to fetch tokens, then redirect to dashboard
        const poll = await pollRegistrationStatus(userId);
        if (poll.ok) {
          localStorage.removeItem('registration_user_id');
          showToast('Your account is active. Welcome!', 'success');
          const dash =
            localStorage.getItem('registration_dashboard_url') || '/';
          localStorage.removeItem('registration_dashboard_url');
          navigate(dash);
        } else {
          setStatusMessage(
            poll.error ||
              'Still activating. Please refresh in a moment.'
          );
          setBusy(false);
        }
        return;
      }

      // Already paid on Paystack, webhook pending
      if (d.already_paid) {
        setWaiting(true);
        setStatusMessage(
          'Payment confirmed. Finishing setup — this can take up to 2 minutes…'
        );
        const poll = await pollRegistrationStatus(userId);
        if (poll.ok) {
          localStorage.removeItem('registration_user_id');
          showToast('Registration complete. Welcome!', 'success');
          const dash =
            localStorage.getItem('registration_dashboard_url') || '/';
          localStorage.removeItem('registration_dashboard_url');
          navigate(dash);
        } else {
          setStatusMessage(
            poll.error ||
              'Payment is still being confirmed. Please check back shortly.'
          );
          setBusy(false);
        }
        return;
      }

      // Normal — Paystack URL
      if (d.authorization_url) {
        if (d.callback_url) {
          localStorage.setItem(
            'registration_dashboard_url',
            d.callback_url
          );
        }
        window.location.href = d.authorization_url;
        return;
      }

      setError('Unexpected response. Please try again.');
      setBusy(false);
    } catch (e) {
      setError('Network error. Please try again.');
      setBusy(false);
    }
  };

  const shell = (inner) => (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: colors.mainBg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '20px 16px' : '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: '-200px',
          right: '-200px',
          width: '600px',
          height: '600px',
          borderRadius: '50%',
          background: `radial-gradient(circle, ${colors.btnBg}08, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          width: '100%',
          maxWidth: 460,
          backgroundColor: colors.raisedBg,
          borderRadius: 20,
          boxShadow: `0 20px 60px ${colors.shadow}`,
          padding: isMobile ? '28px 20px' : '40px 36px',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {inner}
      </div>
    </div>
  );

  if (!userId) {
    return shell(
      <>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            marginBottom: 14,
          }}
        >
          <AlertCircle size={22} color={colors.error} />
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 800,
              color: colors.heading,
              letterSpacing: '-0.01em',
            }}
          >
            Registration not found
          </h1>
        </div>
        <p
          style={{
            margin: '0 0 22px',
            fontSize: 13.5,
            lineHeight: 1.55,
            color: colors.secondaryText,
          }}
        >
          We couldn't identify which account to activate. Please sign in with
          your credentials — we'll take you back here.
        </p>
        <Link
          to="/login"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '12px 16px',
            borderRadius: 10,
            background: colors.btnBg,
            color: colors.btnText,
            fontSize: 14,
            fontWeight: 700,
            textDecoration: 'none',
            fontFamily: 'inherit',
          }}
        >
          Go to sign in
          <ArrowRight size={16} />
        </Link>
      </>
    );
  }

  return shell(
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <span
          style={{
            width: 40,
            height: 40,
            borderRadius: 12,
            background: colors.warningBg || '#FEF3C7',
            color: colors.warning || '#F59E0B',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Coins size={20} />
        </span>
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: isMobile ? 18 : 20,
              fontWeight: 800,
              color: colors.heading,
              letterSpacing: '-0.01em',
            }}
          >
            Activate your account
          </h1>
          <div
            style={{
              fontSize: 12.5,
              color: colors.secondaryText,
              marginTop: 2,
            }}
          >
            Complete your registration payment to unlock agent tools.
          </div>
        </div>
      </div>

      <div
        style={{
          padding: '14px 16px',
          borderRadius: 12,
          background: colors.subtleBg || colors.hoverBg,
          border: `1px solid ${colors.border}`,
          marginBottom: 20,
          fontSize: 13,
          lineHeight: 1.55,
          color: colors.text,
        }}
      >
        Your account exists but the one-time registration fee hasn't been
        paid yet. Until it is, most actions are locked.
      </div>

      {error && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '10px 12px',
            borderRadius: 10,
            background: colors.errorBg || '#FEE2E2',
            color: colors.error || '#DC2626',
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 14,
          }}
        >
          <AlertCircle size={14} style={{ marginTop: 1, flexShrink: 0 }} />
          {error}
        </div>
      )}

      {waiting && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 14px',
            borderRadius: 10,
            background: colors.successBg || '#DCFCE7',
            border: `1px solid ${colors.success || '#16A34A'}55`,
            color: colors.success || '#16A34A',
            fontSize: 12.5,
            fontWeight: 600,
            marginBottom: 14,
          }}
        >
          <Loader2
            size={16}
            style={{ animation: 'ar-spin 1s linear infinite' }}
          />
          <span style={{ flex: 1 }}>{statusMessage}</span>
        </div>
      )}

      <button
        type="button"
        onClick={handleStart}
        disabled={busy || waiting}
        style={{
          width: '100%',
          padding: '14px',
          borderRadius: 12,
          border: 'none',
          background: colors.btnBg,
          color: colors.btnText,
          fontSize: 15,
          fontWeight: 700,
          cursor: busy || waiting ? 'not-allowed' : 'pointer',
          opacity: busy || waiting ? 0.7 : 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          fontFamily: 'inherit',
        }}
      >
        {busy && !waiting ? (
          <>
            <Loader2
              size={18}
              style={{ animation: 'ar-spin 1s linear infinite' }}
            />
            Starting payment…
          </>
        ) : waiting ? (
          <>
            <Loader2
              size={18}
              style={{ animation: 'ar-spin 1s linear infinite' }}
            />
            Waiting for confirmation…
          </>
        ) : (
          <>
            <Shield size={16} />
            Pay registration fee
            <ArrowRight size={16} />
          </>
        )}
      </button>

      <div
        style={{
          marginTop: 14,
          textAlign: 'center',
          fontSize: 12.5,
          color: colors.secondaryText,
        }}
      >
        <Link
          to="/login"
          style={{
            color: colors.btnBg,
            fontWeight: 600,
            textDecoration: 'none',
          }}
        >
          Back to sign in
        </Link>
      </div>

      <style>{`@keyframes ar-spin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
};

export default ActivateRegistration;